import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ExternalLink, Check, Mail, Send } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { currencySymbol } from "@/lib/constants";

const fmtMoney = (v, cur = "USD") =>
  `${currencySymbol(cur)}${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ageDays = (iso) => {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return Math.max(0, Math.floor((Date.now() - t) / 86400000));
};

export default function InvoicesPage() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusTab, setStatusTab] = useState("unpaid"); // unpaid | paid | all
  const [customerFilter, setCustomerFilter] = useState("all"); // customer_id | "all"
  const [sendingStatement, setSendingStatement] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: invs }, { data: custs }] = await Promise.all([
        api.get("/invoices"),
        api.get("/customers"),
      ]);
      setItems(invs);
      setCustomers(custs || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const markPaid = async (id) => {
    try {
      await api.patch(`/invoices/${id}/paid`);
      toast.success("Marked as paid");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const emailInvoice = async (inv) => {
    const target = window.prompt(
      `Email invoice ${inv.invoice_number} to (leave empty to use customer's email):`, "");
    if (target === null) return;
    try {
      const { data } = await api.post(`/invoices/${inv.id}/send-email`,
        { to_email: target || undefined, attach_pod: true });
      toast.success(`Sent to ${data.sent_to}` + (data.pod_attached ? ` (POD attached · ${data.attachment_count})` : ""));
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  // Apply filters
  const filtered = useMemo(() => {
    let out = items;
    if (customerFilter !== "all") out = out.filter((i) => i.customer_id === customerFilter);
    if (statusTab === "unpaid") out = out.filter((i) => i.status !== "paid");
    else if (statusTab === "paid") out = out.filter((i) => i.status === "paid");
    return out;
  }, [items, customerFilter, statusTab]);

  // Aging for the currently selected customer (or all if "all")
  const aging = useMemo(() => {
    const scope = customerFilter === "all"
      ? items
      : items.filter((i) => i.customer_id === customerFilter);
    const unpaid = scope.filter((i) => i.status !== "paid");
    const b = { current: 0, b31_60: 0, b61_90: 0, b90plus: 0, total: 0, count: unpaid.length };
    for (const i of unpaid) {
      const d = ageDays(i.created_at);
      const amt = Number(i.total || 0);
      if (d <= 30) b.current += amt;
      else if (d <= 60) b.b31_60 += amt;
      else if (d <= 90) b.b61_90 += amt;
      else b.b90plus += amt;
      b.total += amt;
    }
    // Pick a currency to display (first invoice's, or USD)
    b.currency = scope[0]?.currency || "USD";
    return b;
  }, [items, customerFilter]);

  const sendStatement = async () => {
    if (customerFilter === "all") {
      toast.error("Select a specific customer to send their statement.");
      return;
    }
    const cust = customers.find((c) => c.id === customerFilter);
    const custName = cust?.company || cust?.name || "this customer";
    if (aging.count === 0) {
      if (!window.confirm(`${custName} has no outstanding invoices. Send a zero-balance statement anyway?`)) return;
    } else {
      if (!window.confirm(
        `Send statement to ${custName}?\n\n` +
        `Total outstanding: ${fmtMoney(aging.total, aging.currency)}\n` +
        `Due within 30 days: ${fmtMoney(aging.current, aging.currency)}\n` +
        `Invoices: ${aging.count}\n\n` +
        `PDF statement will be attached.`
      )) return;
    }
    setSendingStatement(true);
    try {
      const { data } = await api.post(`/customers/${customerFilter}/statement/send`, {});
      toast.success(`Statement sent to ${data.sent_to} — ${fmtMoney(data.total_unpaid, aging.currency)} outstanding`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSendingStatement(false); }
  };

  const selectedCustomerName = customerFilter === "all"
    ? "All customers"
    : (customers.find((c) => c.id === customerFilter)?.company
       || customers.find((c) => c.id === customerFilter)?.name
       || "—");

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="mono-label">ACCOUNTS RECEIVABLE</div>
          <h2 className="text-2xl font-bold tracking-tight mt-1">Invoices</h2>
        </div>
      </div>

      {/* FILTERS + AGING */}
      <div className="border border-zinc-200 bg-white p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="mono-label">Customer</label>
            <Select value={customerFilter} onValueChange={setCustomerFilter}>
              <SelectTrigger className="sharp mt-1 h-10" data-testid="inv-customer-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All customers</SelectItem>
                {customers.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.company || c.name} — {c.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="mono-label">Status</label>
            <Tabs value={statusTab} onValueChange={setStatusTab}>
              <TabsList className="sharp bg-zinc-100 p-0 h-10 mt-1 border border-zinc-950">
                <TabsTrigger value="unpaid" className="sharp px-5 h-full text-xs data-[state=active]:bg-amber-500 data-[state=active]:text-zinc-950" data-testid="inv-tab-unpaid">UNPAID</TabsTrigger>
                <TabsTrigger value="paid" className="sharp px-5 h-full text-xs data-[state=active]:bg-emerald-700 data-[state=active]:text-white" data-testid="inv-tab-paid">PAID</TabsTrigger>
                <TabsTrigger value="all" className="sharp px-5 h-full text-xs data-[state=active]:bg-[#0056B3] data-[state=active]:text-white" data-testid="inv-tab-all">ALL</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div className="flex items-end justify-end">
            <Button
              onClick={sendStatement}
              disabled={sendingStatement || customerFilter === "all"}
              className="sharp bg-[#0056B3] hover:bg-[#004494] h-10"
              title={customerFilter === "all" ? "Select a customer first" : "Email PDF statement to this customer"}
              data-testid="inv-send-statement"
            >
              <Send className="h-4 w-4 mr-2" />
              {sendingStatement ? "Sending..." : "Send Statement"}
            </Button>
          </div>
        </div>

        {/* AGING SUMMARY */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 border-t border-zinc-200 pt-3">
          <div className="border border-zinc-200 bg-zinc-50 p-2 text-center">
            <div className="mono-label !text-[9px]">Current (0-30)</div>
            <div className="font-mono font-bold text-sm mt-1" data-testid="aging-current">{fmtMoney(aging.current, aging.currency)}</div>
          </div>
          <div className="border border-zinc-200 bg-zinc-50 p-2 text-center">
            <div className="mono-label !text-[9px]">31-60 days</div>
            <div className="font-mono font-bold text-sm mt-1">{fmtMoney(aging.b31_60, aging.currency)}</div>
          </div>
          <div className="border border-zinc-200 bg-zinc-50 p-2 text-center">
            <div className="mono-label !text-[9px]">61-90 days</div>
            <div className="font-mono font-bold text-sm mt-1">{fmtMoney(aging.b61_90, aging.currency)}</div>
          </div>
          <div className="border border-rose-200 bg-rose-50 p-2 text-center">
            <div className="mono-label !text-[9px] !text-rose-900">Over 90 days</div>
            <div className="font-mono font-bold text-sm mt-1 text-rose-800">{fmtMoney(aging.b90plus, aging.currency)}</div>
          </div>
          <div className="border-2 border-zinc-950 bg-zinc-950 text-white p-2 text-center">
            <div className="mono-label !text-[9px] !text-zinc-100">TOTAL DUE ({selectedCustomerName.length > 20 ? `${aging.count} inv` : selectedCustomerName})</div>
            <div className="font-mono font-bold text-base mt-1" data-testid="aging-total">{fmtMoney(aging.total, aging.currency)}</div>
          </div>
        </div>
      </div>

      {/* INVOICES TABLE */}
      <div className="border border-zinc-200 bg-white overflow-x-auto">
        <table className="apex-grid">
          <thead>
            <tr>
              <th className="text-left py-3 px-4">Invoice</th>
              <th className="text-left py-3 px-4">Job</th>
              <th className="text-left py-3 px-4">Customer</th>
              <th className="text-left py-3 px-4">Issued</th>
              <th className="text-left py-3 px-4">Age</th>
              <th className="text-left py-3 px-4">Total</th>
              <th className="text-left py-3 px-4">Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="p-8 text-center mono-label">Loading...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={8} className="p-8 text-center mono-label">
                {customerFilter === "all" && statusTab === "unpaid"
                  ? "🎉 No unpaid invoices."
                  : "No invoices match the current filters."}
              </td></tr>
            ) : filtered.map((inv, i) => {
              const days = ageDays(inv.created_at);
              const overdue = inv.status !== "paid" && days > 30;
              return (
                <tr key={inv.id} className={i % 2 ? "bg-zinc-50" : ""} data-testid={`admin-inv-row-${inv.invoice_number}`}>
                  <td className="py-3 px-4 font-mono">{inv.invoice_number}</td>
                  <td className="py-3 px-4 font-mono">
                    <Link to={`/admin/jobs/${inv.job_id}`} className="hover:underline">{inv.job_number}</Link>
                  </td>
                  <td className="py-3 px-4">{inv.customer_company || inv.customer_name}</td>
                  <td className="py-3 px-4 font-mono text-xs">{inv.created_at ? new Date(inv.created_at).toISOString().slice(0, 10) : "—"}</td>
                  <td className="py-3 px-4 font-mono text-xs">
                    <span className={overdue ? "text-rose-700 font-bold" : "text-zinc-700"}>{days}d</span>
                  </td>
                  <td className="py-3 px-4 font-mono font-semibold">{fmtMoney(inv.total, inv.currency)} <span className="text-zinc-500 text-[10px]">{inv.currency || "USD"}</span></td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex px-2.5 py-1 border font-mono text-[10px] uppercase tracking-wider ${
                      inv.status === "paid" ? "bg-emerald-100 text-emerald-900 border-emerald-300" : "bg-amber-100 text-amber-900 border-amber-300"
                    }`}>{inv.status}</span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="inline-flex gap-1">
                      <Link to={`/documents/invoice/${inv.id}`} target="_blank"
                            className="text-[#0056B3] underline text-xs inline-flex items-center">
                        Open <ExternalLink className="h-3 w-3 ml-1" />
                      </Link>
                      <Button size="sm" variant="outline" onClick={() => emailInvoice(inv)}
                        className="sharp h-7 ml-2 border-zinc-300" data-testid={`email-inv-${inv.invoice_number}`}>
                        <Mail className="h-3 w-3 mr-1" />
                        {inv.email_sent_at ? "Resend" : "Email"}
                      </Button>
                      {inv.status !== "paid" && (
                        <Button size="sm" onClick={() => markPaid(inv.id)}
                          className="sharp h-7 ml-1 bg-emerald-700 hover:bg-emerald-800" data-testid={`mark-paid-${inv.invoice_number}`}>
                          <Check className="h-3 w-3 mr-1" /> Paid
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
